import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  getStoredConsent,
  setStoredConsent,
  updateConsent,
} from '../lib/analytics';
import { legalContent } from '../data/mock';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Pequeño retraso para no interrumpir el LCP
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    setStoredConsent('granted');
    updateConsent(true);
    setVisible(false);
  };

  const reject = () => {
    setStoredConsent('denied');
    updateConsent(false);
    setVisible(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="cookie-consent-banner"
            className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div
              className="mx-auto max-w-4xl rounded-2xl bg-white shadow-2xl border p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4"
              style={{ borderColor: 'rgba(91, 154, 111, 0.25)' }}
            >
              <div
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(91, 154, 111, 0.15)' }}
              >
                <Cookie className="w-6 h-6" style={{ color: '#5B9A6F' }} />
              </div>
              <div className="flex-1 text-sm text-gray-700 leading-relaxed">
                <p className="font-semibold text-gray-800 mb-1">
                  Usamos cookies de analítica
                </p>
                <p>
                  Utilizamos Google Analytics para entender cómo se usa la web y
                  mejorarla. No compartimos datos personales. Puedes aceptar o
                  rechazar su uso.{' '}
                  <button
                    type="button"
                    onClick={() => setShowPolicy(true)}
                    className="underline font-medium"
                    style={{ color: '#5B9A6F' }}
                    data-testid="cookie-consent-policy-link"
                  >
                    Más información
                  </button>
                  .
                </p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  onClick={reject}
                  className="rounded-full px-5 flex-1 md:flex-none"
                  data-testid="cookie-consent-reject"
                >
                  Rechazar
                </Button>
                <Button
                  onClick={accept}
                  className="btn-primary rounded-full px-5 flex-1 md:flex-none"
                  data-testid="cookie-consent-accept"
                >
                  Aceptar
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showPolicy && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPolicy(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">
                {legalContent.cookies?.title || 'Política de Cookies'}
              </h3>
              <button
                onClick={() => setShowPolicy(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="cookie-policy-close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                {legalContent.cookies?.content}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
